/**
 * Encounter view model for the Billing screen table.
 *
 * Shaped after the FHIR Encounter resource
 * (https://www.medplum.com/docs/api/fhir/resources/encounter) so it can be swapped
 * for live Medplum data later with minimal changes.
 */

export type EncounterStatus =
  | 'planned'
  | 'arrived'
  | 'triaged'
  | 'in-progress'
  | 'onleave'
  | 'finished'
  | 'cancelled';

/** ChargeItem.status (subset we surface in the billing UI). */
export type ChargeItemStatus = 'billable' | 'billed' | 'not-billable';

/** Flat view model for one ChargeItem referencing an Encounter (ChargeItem.context). */
export interface ChargeItem {
  /** ChargeItem.code coding (CPT/HCPCS). */
  code: string;
  /** ChargeItem.code display. */
  description: string;
  /** ChargeItem.quantity. */
  quantity: number;
  /** ChargeItem.priceOverride amount (USD). */
  unitPrice: number;
  /** ChargeItem.status. */
  status: ChargeItemStatus;
}

/** Flat, searchable/filterable view model for one Encounter row. */
export interface EncounterRow {
  id: string;
  /** Encounter.identifier */
  identifier: string;
  /** Encounter.subject (Patient) display */
  patientName: string;
  /** Encounter.participant.individual (Practitioner) display */
  provider: string;
  /** Encounter.status */
  status: EncounterStatus;
  /** Encounter.class display */
  className: string;
  /** Encounter.type display */
  type: string;
  /** Encounter.serviceType display */
  serviceType: string;
  /** Billing code from Encounter.type coding */
  cptCode: string;
  /** Encounter.reasonCode display */
  reason: string;
  /** Encounter.period.start (ISO) */
  start: string;
  /** Encounter.period.end (ISO) */
  end: string;
  /** Derived from Encounter.length / period (minutes) */
  lengthMinutes: number;
  /** Encounter.serviceProvider (Organization) display */
  serviceProvider: string;
  /** Encounter.episodeOfCare display */
  episodeOfCare: string;
  /** Encounter.appointment id this encounter originated from (FHIR Encounter.appointment). */
  appointmentId: string;
  /** ChargeItem resources referencing this Encounter (ChargeItem.context). */
  chargeItems: ChargeItem[];
}

export const MOCK_ENCOUNTERS: EncounterRow[] = [];
